import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useBrand } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-10">
      <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-3">{title}</h2>
      <div className="text-slate-400 text-sm sm:text-base leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

// Meta (Facebook) requires either a Data Deletion Request Callback URL or a
// Data Deletion Instructions URL for any app using Facebook Login products.
// This page is the instructions URL, and also renders the status of a
// specific request when Meta's callback (see backend-node/src/routes/
// metaRoutes.js) redirects a Facebook user here with a confirmation_code —
// the same page serves both purposes, per Meta's own documented pattern.
export default function DataDeletion() {
  const brand = useBrand();
  const [params] = useSearchParams();
  const confirmationCode = params.get("confirmation_code");
  const [status, setStatus] = useState(confirmationCode ? "loading" : null);
  const [statusData, setStatusData] = useState(null);

  useSeo({
    title: `Data Deletion | ${brand.name}`,
    description: `How to request deletion of your data from ${brand.name}, including for the ${brand.name} Facebook app.`,
    canonical: `https://${brand.domain}/data-deletion`,
  });

  useEffect(() => {
    if (!confirmationCode) return;
    let cancelled = false;
    api
      .get(`/meta/data-deletion/${encodeURIComponent(confirmationCode)}`)
      .then(({ data }) => {
        if (!cancelled) {
          setStatusData(data);
          setStatus("found");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("not-found");
      });
    return () => {
      cancelled = true;
    };
  }, [confirmationCode]);

  return (
    <div>
      <PageHeader
        label="Legal"
        title="Data Deletion"
        sub={`How to request that ${brand.name} delete the information it holds about you, including data received through the ${brand.name} Facebook app.`}
      />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {confirmationCode && (
          <Section id="status" title="Your Deletion Request Status">
            {status === "loading" && <p>Checking your request…</p>}
            {status === "not-found" && (
              <p>
                We could not find a request matching confirmation code{" "}
                <span className="font-mono text-slate-300">{confirmationCode}</span>. If you believe
                this is an error, contact us using the details in the section below.
              </p>
            )}
            {status === "found" && statusData && (
              <div className="card-surface p-6 space-y-2">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 size={18} />
                  <span className="font-semibold">Request received</span>
                </div>
                <div className="text-sm">
                  Confirmation code: <span className="font-mono text-slate-300">{statusData.id}</span>
                </div>
                <div className="text-sm">
                  Status: <span className="text-slate-300 capitalize">{statusData.status}</span>
                </div>
                <div className="text-sm">
                  Requested: <span className="text-slate-300">{statusData.requested_at}</span>
                </div>
              </div>
            )}
          </Section>
        )}

        <Section id="facebook" title="1. Deleting Data Received Through Facebook Login">
          <p>
            If you have used Facebook Login to interact with a {brand.name} app or integration, you can
            ask Facebook itself to remove the app's access to your data (Facebook Settings → Apps and
            Websites → remove the {brand.name} app), which triggers Meta's own data deletion process on
            their side.
          </p>
          <p>
            You can also request deletion directly from us — see Section 3 below. Either path results in
            any data we received through that integration being deleted from our systems.
          </p>
        </Section>

        <Section id="what-we-delete" title="2. What Gets Deleted">
          <p>
            A deletion request removes: any enquiry/lead record you submitted through this Site's Contact
            or Get a Quote forms, and any data received about you through a connected Facebook/Meta
            integration (such as a Facebook user identifier). We do not retain backups specifically to
            defeat a deletion request; any data required to be kept for legal, accounting or fraud-
            prevention reasons is retained only for as long as that requirement applies, consistent with
            our Privacy Policy's retention section.
          </p>
        </Section>

        <Section id="request" title="3. How To Request Deletion">
          <p>
            To request deletion of your data, contact us using any of the details below and mention
            "data deletion request" along with the name, phone number or email you used when submitting
            information to us. We will confirm once the request has been actioned.
          </p>
          <div className="card-surface p-6 mt-2 space-y-3 text-sm not-italic">
            <div className="text-white font-semibold">{brand.name}</div>
            <div className="flex items-start gap-3">
              <MapPin className="text-emerald-400 shrink-0" size={18} />
              <span>See our Contact page for the current address on file.</span>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="text-emerald-400 shrink-0" size={18} />
              <span>See our Contact page for the current phone number.</span>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="text-emerald-400 shrink-0" size={18} />
              <span>Email is the fastest way to reach us — see our Contact page for the current address.</span>
            </div>
          </div>
        </Section>

        <Section id="timeline" title="4. Timeline">
          <p>
            We aim to action deletion requests within a reasonable timeframe after verifying the request.
            A request submitted through the Facebook data-deletion mechanism is assigned a confirmation
            code immediately (shown at the top of this page when you arrive here via that link); a
            request submitted by email or phone is confirmed directly with you.
          </p>
        </Section>

        <Section id="privacy-policy" title="5. Related">
          <p>
            See our{" "}
            <a href="/privacy-policy" className="text-emerald-400 hover:underline">
              Privacy Policy
            </a>{" "}
            for full details on what information we collect and how it is used.
          </p>
        </Section>
      </section>
    </div>
  );
}
